import type { ReportSection } from "~/types/global";

/**
 * Reconciliação de edição concorrente entre abas (#21909).
 *
 * A tela de detalhes NÃO insere conteúdo novo: toda edição do usuário é uma
 * marcação de remoção — uma entrada ou uma seção inteira vira `is_deleted`
 * (ver `store/details/actions/updateActions.ts`, que só muta `is_deleted` e
 * `data_count`; o conteúdo em si nunca é criado na tela). Isso torna o merge
 * de um conflito determinístico: quando a aba A e a aba B removem coisas
 * diferentes do mesmo relatório, o resultado consistente é a UNIÃO das
 * remoções — exatamente "o conjunto completo das edições" que o bug pede.
 *
 * Após um 409 (outra aba salvou primeiro), a base nova vem do servidor (já
 * com as remoções da outra aba). Esta função reaplica, por cima dessa base,
 * as remoções que ESTA aba fez localmente e que ainda não tinham sido
 * persistidas — casando seção por `title`+`subsection` e entrada por índice
 * de conteúdo estável. Nunca "ressuscita" o que o servidor removeu (OR das
 * flags), nem perde o que esta aba removeu.
 *
 * É puro e defensivo: entradas desconhecidas no servidor são ignoradas sem
 * lançar, e a ordem/estrutura do servidor é preservada.
 */

const sectionKey = (s: Pick<ReportSection, "title" | "subsection">): string =>
  `${s.title ?? ""}\u0000${s.subsection ?? ""}`;

/**
 * Chave de identidade de uma entrada dentro de uma seção. Preferimos um id
 * estável quando existe; senão caímos no JSON ordenado dos campos (as entradas
 * de um relatório são imutáveis em conteúdo, então isso é estável entre um
 * fetch e o anterior). Igual à estratégia de `mergeSectionData.helper.ts`.
 */
const entryKey = (entry: any): string => {
  if (entry && typeof entry === "object") {
    const id = entry.id ?? entry.entry_id ?? entry.interface_id;
    if (id != null) return `id:${String(id)}`;
  }
  try {
    return `j:${JSON.stringify(entry)}`;
  } catch {
    return `j:${String(entry)}`;
  }
};

export interface ReconcileResult {
  sections: ReportSection[];
  /** Quantas remoções locais foram reaplicadas sobre a base do servidor. */
  reappliedCount: number;
}

export function reconcileSectionsAfterConflict(
  localSections: ReportSection[] | undefined | null,
  serverSections: ReportSection[] | undefined | null,
): ReconcileResult {
  const local = Array.isArray(localSections) ? localSections : [];
  const server = Array.isArray(serverSections) ? serverSections : [];

  // Index das seções locais por chave, para lookup O(1).
  const localByKey = new Map<string, ReportSection>();
  for (const s of local) localByKey.set(sectionKey(s), s);

  let reappliedCount = 0;

  const merged = server.map((serverSection) => {
    const localSection = localByKey.get(sectionKey(serverSection));
    if (!localSection) return serverSection;

    // Seção inteira marcada como removida localmente: reaplica (OR).
    const sectionDeletedLocally = !!localSection.is_deleted && !serverSection.is_deleted;

    // Mapa das entradas removidas localmente, por identidade.
    const locallyDeletedEntries = new Set<string>();
    if (Array.isArray(localSection.data)) {
      for (const entry of localSection.data) {
        if (entry && typeof entry === "object" && (entry as any).is_deleted) {
          locallyDeletedEntries.add(entryKey(entry));
        }
      }
    }

    if (!sectionDeletedLocally && locallyDeletedEntries.size === 0) {
      return serverSection;
    }

    const nextData = Array.isArray(serverSection.data)
      ? serverSection.data.map((entry: any) => {
          if (
            entry &&
            typeof entry === "object" &&
            !entry.is_deleted &&
            locallyDeletedEntries.has(entryKey(entry))
          ) {
            reappliedCount += 1;
            return { ...entry, is_deleted: true };
          }
          return entry;
        })
      : serverSection.data;

    if (sectionDeletedLocally) reappliedCount += 1;

    return {
      ...serverSection,
      data: nextData,
      is_deleted: serverSection.is_deleted || sectionDeletedLocally,
    };
  });

  return { sections: merged, reappliedCount };
}
